import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationApi } from '../../services/api/OrganizationApi';
import './MyOrganizationsPage.css';

/**
 * MyOrganizationsPage
 *
 * Lists every organization the current user has an active admin role in
 * (org_admin or course_admin). Each card links to that org's admin dashboard.
 * Shown when a user admins more than one org; users who admin exactly one org
 * are routed directly to that org's dashboard from the sidebar.
 */
export default function MyOrganizationsPage() {
  const { user, orgMemberships, hasAnyOrgAdminRole } = useAuth();
  const isGlobalAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const adminMemberships = useMemo(() => (orgMemberships || []).filter(m =>
    m.status === 'active' && (m.orgRole === 'org_admin' || m.orgRole === 'course_admin')
  ), [orgMemberships]);

  const [orgNames, setOrgNames] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(adminMemberships.map(async (m) => {
        // Prefer name already on the membership payload
        if (m.organizationName) {
          next[m.organizationId] = m.organizationName;
          return;
        }
        try {
          const resp = await OrganizationApi.getOrganization(m.organizationId);
          if (!resp.ok) return;
          const data = await resp.json();
          next[m.organizationId] = data?.name || data?.organizationName;
        } catch {
          /* ignore — fall back to id-based label */
        }
      }));
      if (!cancelled) setOrgNames(prev => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [adminMemberships]);

  if (!isGlobalAdmin && !hasAnyOrgAdminRole()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="my-orgs-page">
      <header className="my-orgs-header">
        <h1>My Organizations</h1>
        <p className="my-orgs-subtitle">Pick an organization to manage its members, courses and domains.</p>
      </header>

      {adminMemberships.length === 0 ? (
        <div className="my-orgs-empty">
          You don't currently administer any organizations.
        </div>
      ) : (
        <div className="my-orgs-grid">
          {adminMemberships.map(m => {
            const id = Number(m.organizationId);
            const label = orgNames[id] || `Organization #${id}`;
            const roleLabel = m.orgRole === 'org_admin' ? 'Org Admin' : 'Course Admin';
            return (
              <Link key={id} to={`/organization/${id}/admin`} className="my-orgs-card">
                <div className="my-orgs-card-icon" aria-hidden="true">🏢</div>
                <div className="my-orgs-card-body">
                  <div className="my-orgs-card-title">{label}</div>
                  <div className="my-orgs-card-meta">
                    <span className={`my-orgs-role-badge ${m.orgRole}`}>{roleLabel}</span>
                  </div>
                </div>
                <div className="my-orgs-card-cta" aria-hidden="true">→</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
